import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Flask,
  CheckCircle,
  User,
  EnvelopeSimple,
  Buildings,
  Briefcase,
  Sparkle,
  Confetti,
  Warning,
  NumberCircleOne,
  NumberCircleTwo,
  NumberCircleThree,
  NumberCircleFour,
  NumberCircleFive,
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

const WORKFLOW_STEPS = [
  {
    icon: NumberCircleOne,
    title: "Anmeldeformular ausfüllen",
    text: "Du gibst Name und deine Google-Konto E-Mail-Adresse ein. Dauert 30 Sekunden.",
    timing: "Jetzt sofort",
  },
  {
    icon: NumberCircleTwo,
    title: "Kurze Wartezeit",
    text: "Wir sammeln die ersten 12 Anmeldungen und bereiten den Test in der Google Play Console vor.",
    timing: "1-3 Tage",
  },
  {
    icon: NumberCircleThree,
    title: "Einladung von Google Play",
    text: "Du erhältst eine E-Mail direkt von Google Play mit einem Test-Teilnahme-Link.",
    timing: "1-5 Tage nach Anmeldung",
  },
  {
    icon: NumberCircleFour,
    title: "Testteilnahme + Installation",
    text: "Link klicken → 'Testteilnehmer werden' → Plantus aus dem Play Store installieren.",
    timing: "2 Minuten",
  },
  {
    icon: NumberCircleFive,
    title: "App nutzen + Feedback",
    text: "14 Tage Testphase. Du bekommst automatisch Plantus Pro 6 Monate kostenlos freigeschaltet.",
    timing: "Ab Installation",
  },
];

export const BetaSignup = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "",
    company: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const update = (k, v) => setForm((f) => ({...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Bitte Name und E-Mail angeben");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/beta-signup`, form);
      setSuccess(data);
      if (data.already_registered) {
        toast.info("Du bist schon in der Warteliste 🌱");
      } else {
        toast.success("Anmeldung erhalten! Wir melden uns bald bei dir.");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          "Anmeldung fehlgeschlagen. Bitte später versuchen."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="beta"
      data-testid="beta-signup-section"
      className="relative py-24 sm:py-28 bg-bone overflow-hidden scroll-mt-24"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Linke Seite: Text */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-6"
        >
          <div className="inline-flex items-center gap-2 rounded-full bg-terracotta/10 border border-terracotta/25 text-terracotta px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] mb-5">
            <Flask size={14} weight="duotone" /> Google Play Beta
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-forest-900 leading-[1.1]">
            Sei einer der{" "}
            <span className="text-terracotta">ersten 100 Beta-Tester</span>
          </h2>
          <p className="mt-5 font-body text-base sm:text-lg text-[#4A524C] leading-relaxed">
            Plantus geht in den offiziellen Google Play Store Beta-Test. Als
            Dankeschön bekommst du{" "}
            <strong className="text-forest-900">
              Plantus Pro 6 Monate kostenlos
            </strong>{" "}, das sind rund <strong>60 € Ersparnis</strong>.
          </p>

          <ul className="mt-8 space-y-3">
            {[
              "Zugang zur App direkt aus dem Play Store (kein APK-Installations-Hack)",
              "Volle Plantus-Pro-Features während der Beta-Zeit",
              "6 Monate kostenlos nach Freigabe (99 € Wert)",
              "Direkter Draht zum Entwickler bei Fragen und Wünschen",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <CheckCircle
                  size={20}
                  weight="fill"
                  className="text-forest-700 mt-0.5 flex-shrink-0"
                />
                <span className="font-body text-[15px] text-[#4A524C]">
                  {t}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex items-center gap-3 text-xs text-[#7A827C]">
            <Sparkle size={14} weight="fill" className="text-terracotta" />
            <span>
              Voraussetzung: Android-Gerät + Google-Konto (für Play Store)
            </span>
          </div>
        </motion.div>

        {/* Rechte Seite: Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-6"
        >
          {success ? (
            <div
              className="bg-white rounded-3xl border border-forest-200 p-8 sm:p-10 text-center shadow-sm"
              data-testid="beta-success"
            >
              <div className="w-16 h-16 rounded-2xl bg-forest-100 flex items-center justify-center mx-auto mb-5">
                <Confetti size={32} weight="duotone" className="text-forest-700" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-forest-900">
                {success.already_registered
                  ? "Du bist schon dabei 🌱"
                  : "Anmeldung erfolgreich!"}
              </h3>
              <p className="mt-3 font-body text-[15px] text-[#4A524C]">
                {success.already_registered
                  ? "Deine E-Mail-Adresse ist bereits in unserer Warteliste."
                  : "Danke! Du bist jetzt in der Warteliste. Sobald wir startklar sind, bekommst du eine Einladung von Google Play direkt an deine E-Mail."}
              </p>
              {typeof success.total === "number" && (
                <p className="mt-4 inline-block bg-forest-50 text-forest-800 text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full">
                  Aktuell {success.total} Beta-Tester in der Warteliste
                </p>
              )}
              <button
                onClick={() => {
                  setSuccess(null);
                  setForm({ name: "", email: "", role: "", company: "" });
                }}
                className="mt-6 text-sm text-forest-700 hover:text-forest-900 underline"
              >
                Noch jemand anmelden
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="beta-signup-form"
              className="bg-white rounded-3xl border border-bone-200 p-6 sm:p-8 shadow-sm"
            >
              <h3 className="font-heading text-xl font-semibold text-forest-900 mb-1">
                Jetzt für die Beta anmelden
              </h3>
              <p className="font-body text-sm text-[#7A827C] mb-6">
                Nur 30 Sekunden, wir sammeln nichts weiter als das Nötigste.
              </p>

              <div className="space-y-4">
                <FormField
                  icon={User}
                  label="Dein Name *"
                  value={form.name}
                  onChange={(v) => update("name", v)}
                  placeholder="Max Mustermann"
                  testId="beta-name"
                  required
                />
                <FormField
                  icon={EnvelopeSimple}
                  label="Google-Konto E-Mail *"
                  type="email"
                  value={form.email}
                  onChange={(v) => update("email", v)}
                  placeholder="max@mustermann.de"
                  hint="Die E-Mail deines Google-Play-Kontos"
                  testId="beta-email"
                  required
                />
                <div className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 p-3">
                  <Warning
                    size={18}
                    weight="fill"
                    className="text-amber-600 mt-0.5 flex-shrink-0"
                  />
                  <div className="text-xs text-amber-900 leading-relaxed">
                    <strong>Wichtig:</strong> Die E-Mail muss ein{" "}
                    <strong>Google-Konto</strong> sein (Gmail oder mit Google
                    verknüpft). Sonst kann Google Play dir keine Einladung
                    schicken. Nicht sicher?{" "}
                    <a
                      href="https://accounts.google.com/signup"
                      target="_blank"
                      rel="noreferrer"
                      className="underline hover:text-amber-700"
                    >
                      Google-Konto erstellen
                    </a>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    icon={Briefcase}
                    label="Rolle (optional)"
                    value={form.role}
                    onChange={(v) => update("role", v)}
                    placeholder="Landwirt, Winzer, …"
                    testId="beta-role"
                  />
                  <FormField
                    icon={Buildings}
                    label="Betrieb (optional)"
                    value={form.company}
                    onChange={(v) => update("company", v)}
                    placeholder="Musterhof GbR"
                    testId="beta-company"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="beta-submit"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-forest-700 hover:bg-forest-800 disabled:opacity-60 text-white font-heading font-semibold px-6 py-3.5 rounded-xl transition shadow-sm"
              >
                <Flask size={18} weight="duotone" />
                {submitting ? "Sende …" : "Als Beta-Tester anmelden"}
              </button>
              <p className="mt-3 text-xs text-[#7A827C] text-center">
                Kein Spam. Kein Weiterverkauf. Jederzeit abmeldbar.
              </p>
            </form>
          )}
        </motion.div>
      </div>

      {/* Ablaufplan, wie geht's weiter? */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-12"
        >
          <div className="inline-flex items-center gap-2 rounded-full bg-forest-50 border border-forest-200 text-forest-800 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] mb-4">
            <Flask size={14} weight="duotone" /> So läuft's ab
          </div>
          <h3 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-forest-900">
            Was passiert nach der Anmeldung?
          </h3>
          <p className="mt-4 font-body text-[15px] text-[#4A524C] leading-relaxed">
            Damit du weißt, was auf dich zukommt, der komplette Weg von der
            Anmeldung bis zur installierten App.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-0 relative">
          {WORKFLOW_STEPS.map((step, i) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`workflow-step-${i + 1}`}
              className="relative bg-white rounded-2xl border border-bone-200 p-5 lg:mx-2 hover:border-forest-600/40 hover:shadow-sm transition"
            >
              <div className="flex items-center gap-3 mb-3">
                <step.icon
                  size={30}
                  weight="duotone"
                  className="text-forest-700 flex-shrink-0"
                />
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-terracotta">
                  Schritt {i + 1}
                </div>
              </div>
              <h4 className="font-heading text-base font-semibold text-forest-900 leading-snug mb-1.5">
                {step.title}
              </h4>
              <p className="font-body text-sm text-[#4A524C] leading-relaxed">
                {step.text}
              </p>
              <div className="mt-3 font-body text-xs text-[#7A827C] flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-terracotta" />
                {step.timing}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Wichtige Info-Karte darunter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-10 rounded-2xl bg-forest-900 text-white p-6 sm:p-8 flex flex-wrap items-start gap-5"
        >
          <div className="w-12 h-12 rounded-xl bg-lime-400/20 flex items-center justify-center flex-shrink-0">
            <Warning size={22} weight="duotone" className="text-lime-300" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-heading text-lg font-semibold text-white">
              Warum ein Google-Konto zwingend nötig ist
            </div>
            <p className="mt-2 font-body text-sm text-white/70 leading-relaxed">
              Google Play verschickt die Test-Einladungen{" "}
              <strong className="text-white">
                ausschließlich an registrierte Google-Konten
              </strong>. Wenn du deine T-Online-, GMX- oder Web.de-Adresse angibst und
              damit kein Google-Konto verknüpft hast, kommt die Einladung
              nirgends an. Nutze idealerweise deine{" "}
              <strong className="text-white">Gmail-Adresse</strong>, oder{" "}
              <a
                href="https://accounts.google.com/signup"
                target="_blank"
                rel="noreferrer"
                className="text-lime-300 hover:text-lime-200 underline"
              >
                erstelle in 2 Minuten ein neues Google-Konto
              </a>. Kostet nichts, dauert nicht länger als ein Kaffee.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const FormField = ({
  icon: Icon,
  label,
  value,
  onChange,
  testId,
  hint,
  type = "text",...props
}) => (
  <div>
    <label className="block font-body text-sm font-medium text-forest-900 mb-1.5">
      {label}
    </label>
    <div className="relative">
      <Icon
        size={16}
        className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7A827C]"
      />
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full pl-10 pr-3.5 py-2.5 font-body text-[15px] rounded-xl border border-bone-200 bg-bone/50 focus:outline-none focus:border-forest-600 focus:ring-2 focus:ring-forest-600/15 transition"
        {...props}
      />
    </div>
    {hint && (
      <p className="mt-1 text-xs text-[#7A827C]">{hint}</p>
    )}
  </div>
);
